import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { sendFailureNotification } from "shared/toast_notifications"
import { allLocalesStore } from "../../../stores/locales/all_locales_store"
import { mergeLocaleHierarchy } from "../../../stores/locales/merge_locale_hierarchy"

// Max number of background locale sub-requests (buildings/areas/rooms) allowed in
// flight at once across the ENTIRE hierarchy fetch. Capping at 3 keeps this background
// prefetch from monopolizing the pools while still progressing quickly. Tune here
// if needed.
const LOCALE_FETCH_CONCURRENCY_LIMIT = 3

/**
 * Supplier controller for the complete location hierarchy
 *
 * This controller fetches the complete location hierarchy (regions → buildings → areas → rooms)
 * on page load and populates the allLocalesStore for instant access by the permissions modal.
 *
 * Connects to data-controller="suppliers--locales--all-locales-supplier"
 *
 * Optional org-scope: pages that need the FULL organization locale namespace (not just the
 * caller's active-project locales) set the projectScopeToken value to a short-lived, signed
 * organization-scope token minted server-side behind an authorization check (e.g. the Projects
 * management page, see ProjectsController#manage). When present, it is forwarded as the
 * X-Project-Scope-Token header on every locale-list request so the server widens results to
 * scope: :organization. Left unset (e.g. the onboarding setup page, the permissions modal), no
 * header is sent and the requests stay active-project scoped exactly as before.
 */

// Module-scoped (NOT per-instance) promise for the in-flight hierarchy load. Every supplier
// instance on the page — and across Turbo visits, since the JS context survives navigation —
// shares one load instead of starting a second concurrent crawl. The .finally() reset ensures
// a failed load does not permanently block all future loads.
let hierarchyLoadPromise = null

export default class extends Controller {

    static stores = [
        allLocalesStore
    ]

    static values = {
        projectScopeToken: String
    }

    connect() {
        useStore(this)

        // Check if data is already loaded
        const currentStore = this.allLocalesStoreValue

        if (!currentStore.regionsLoaded) {
            // A load is already running (possibly from another instance or a prior Turbo visit);
            // its store writes will notify us via onAllLocalesStoreUpdate — don't start a second.
            if (hierarchyLoadPromise) return

            // Cover the WHOLE load (regions + background crawl) so a second connect during the
            // background phase can't start a duplicate. .finally() resets the guard so a future
            // connect or refreshHierarchy is not blocked after this load finishes or fails.
            hierarchyLoadPromise = this.fetchRegionsFirst().finally(() => { hierarchyLoadPromise = null })
        }
    }

    /**
     * Fetch regions first and display them immediately, then load remaining locations in background
     *
     * This method:
     * 1. Fetches all regions (with pagination)
     * 2. Immediately updates store with regions and sets regionsLoaded: true
     * 3. Kicks off background fetch for buildings, areas, and rooms
     */
    async fetchRegionsFirst() {
        try {
            // Step 1: Fetch all regions (with pagination)
            const regionsData = await this.fetchAllRegions()

            // Handle empty regions
            if (regionsData.length === 0) {
                console.warn('No regions returned from API')
                // Still update store with empty data
                this.setAllLocalesStoreValue(mergeLocaleHierarchy(this.allLocalesStoreValue, {
                    lastRefreshTime: new Date().toISOString(),
                    regionsLoaded: true,
                    initialLoadComplete: true,
                    locales: {
                        regions: []
                    }
                }))
                return
            }

            // Convert API format (text) to store format (name)
            const regions = regionsData.map(region => ({
                id: region.id,
                name: region.text,  // Convert 'text' to 'name'
                locale: region.locale,
                buildings: []  // Will be populated by background fetch
            }))

            // Step 2: Immediately update store with regions so tree can render
            this.setAllLocalesStoreValue(mergeLocaleHierarchy(this.allLocalesStoreValue, {
                lastRefreshTime: new Date().toISOString(),
                regionsLoaded: true,
                initialLoadComplete: false,  // Not complete yet
                locales: {
                    regions: regions
                }
            }))

            // Step 3: Start background fetch for remaining locations.
            // AWAIT it so the module-level hierarchyLoadPromise guard covers the WHOLE load
            // (regions + full crawl) — otherwise this method resolves after only the regions
            // phase and a second instance could still start a duplicate background crawl. This
            // does NOT delay the UI: the regions store write above already happened, so the tree
            // renders as soon as regions land. Do NOT remove this await.
            await this.fetchRemainingLocationsInBackground(regions)

        } catch (error) {
            console.error('Error loading regions:', {
                error: error.message,
                name: error.name,
                timestamp: new Date().toISOString()
            })

            // Handle specific error types with user-friendly messages
            let errorMessage = 'Unable to load facility information. Please refresh the page.'

            if (error.name === 'TimeoutError' || error.name === 'AbortError') {
                errorMessage = 'Request timed out while loading locations. Please refresh the page.'
            } else if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
                errorMessage = 'Unable to connect to server. Please check your connection and refresh the page.'
            } else if (error.message === 'Malformed API response') {
                errorMessage = 'Received invalid data from server. Please refresh the page.'
            }

            // Show user-friendly error message
            sendFailureNotification(errorMessage)

            // Keep regionsLoaded as false on error
        }
    }

    /**
     * Fetch remaining locations (buildings, areas, rooms) in the background
     *
     * This method:
     * 1. For each region, fetches all buildings in parallel (with pagination)
     * 2. For each building, fetches all areas in parallel (with pagination)
     * 3. For each area, fetches all rooms in parallel (with pagination)
     * 4. Updates the store incrementally as data arrives
     * 5. Sets initialLoadComplete: true when done
     */
    async fetchRemainingLocationsInBackground(regions) {
        try {
            // Step 1: Fetch buildings for each region, at most
            // LOCALE_FETCH_CONCURRENCY_LIMIT requests in flight at once. The region
            // list is already flat, so a single bounded pass suffices.
            const regionsWithBuildings = await this.mapWithConcurrencyLimit(
                regions,
                LOCALE_FETCH_CONCURRENCY_LIMIT,
                async (region) => {
                    try {
                        const buildingsData = await this.fetchAllBuildings(region.id)

                        // Convert API format (text) to store format (name)
                        const buildings = buildingsData.map(building => ({
                            id: building.id,
                            name: building.text,  // Convert 'text' to 'name'
                            locale: building.locale,
                            areas: []  // Will be populated next
                        }))

                        return { ...region, buildings }
                    } catch (error) {
                        console.error(`Error fetching buildings for region ${ region.id }:`, {
                            error: error.message,
                            name: error.name,
                            timestamp: new Date().toISOString()
                        })
                        // childrenLoadFailed distinguishes a FAILED fetch from a genuinely
                        // empty region. Without it, after the crawl finishes this branch would
                        // look legitimately childless (no chevron, no retry); the flag keeps the
                        // expand chevron / on-demand retry available.
                        return { ...region, buildings: [], childrenLoadFailed: true }
                    }
                }
            )

            // Step 2: Fetch areas for each building. Flatten every building across ALL
            // regions into one list and run it through a SINGLE bounded pass, so total
            // concurrency across the whole stage stays <= LOCALE_FETCH_CONCURRENCY_LIMIT
            // (nesting a per-region Promise.all around a per-building one is what made
            // the old fan-out unbounded).
            const buildingTasks = []
            regionsWithBuildings.forEach((region, regionIndex) => {
                region.buildings.forEach((building, buildingIndex) => {
                    buildingTasks.push({ region, regionIndex, building, buildingIndex })
                })
            })

            const buildingsWithAreasFlat = await this.mapWithConcurrencyLimit(
                buildingTasks,
                LOCALE_FETCH_CONCURRENCY_LIMIT,
                async (task) => {
                    try {
                        const areasData = await this.fetchAllAreas(task.region.id, task.building.id)

                        // Convert API format (text) to store format (name)
                        const areas = areasData.map(area => ({
                            id: area.id,
                            name: area.text,  // Convert 'text' to 'name'
                            locale: area.locale,
                            rooms: []  // Will be populated next
                        }))

                        return { ...task, building: { ...task.building, areas } }
                    } catch (error) {
                        console.error(`Error fetching areas for building ${ task.building.id }:`, {
                            error: error.message,
                            name: error.name,
                            timestamp: new Date().toISOString()
                        })
                        // Mark FAILED (not genuinely empty) so the chevron / on-demand retry
                        // stays available for this building even after the crawl completes.
                        return { ...task, building: { ...task.building, areas: [], childrenLoadFailed: true } }
                    }
                }
            )

            // Reassemble the flat results back into the region → building hierarchy,
            // preserving original ordering via the captured indices.
            const regionsWithAreas = regionsWithBuildings.map(region => ({
                ...region,
                buildings: [...region.buildings]
            }))
            buildingsWithAreasFlat.forEach(result => {
                regionsWithAreas[result.regionIndex].buildings[result.buildingIndex] = result.building
            })

            // Step 3: Fetch rooms for each area. Same flatten-then-bounded-pass approach,
            // across every area in ALL buildings in ALL regions.
            const areaTasks = []
            regionsWithAreas.forEach((region, regionIndex) => {
                region.buildings.forEach((building, buildingIndex) => {
                    building.areas.forEach((area, areaIndex) => {
                        areaTasks.push({ region, regionIndex, building, buildingIndex, area, areaIndex })
                    })
                })
            })

            const areasWithRoomsFlat = await this.mapWithConcurrencyLimit(
                areaTasks,
                LOCALE_FETCH_CONCURRENCY_LIMIT,
                async (task) => {
                    try {
                        const roomsData = await this.fetchAllRooms(task.region.id, task.building.id, task.area.id)

                        // Convert API format (text) to store format (name)
                        const rooms = roomsData.map(room => ({
                            id: room.id,
                            name: room.text,  // Convert 'text' to 'name'
                            locale: room.locale
                        }))

                        return { ...task, area: { ...task.area, rooms } }
                    } catch (error) {
                        console.error(`Error fetching rooms for area ${ task.area.id }:`, {
                            error: error.message,
                            name: error.name,
                            timestamp: new Date().toISOString()
                        })
                        // Mark FAILED (not genuinely empty) so the chevron / on-demand retry
                        // stays available for this area even after the crawl completes.
                        return { ...task, area: { ...task.area, rooms: [], childrenLoadFailed: true } }
                    }
                }
            )

            // Reassemble the flat room results back into the full hierarchy, preserving
            // ordering via the captured indices.
            const regionsWithRooms = regionsWithAreas.map(region => ({
                ...region,
                buildings: region.buildings.map(building => ({ ...building, areas: [...building.areas] }))
            }))
            areasWithRoomsFlat.forEach(result => {
                regionsWithRooms[result.regionIndex].buildings[result.buildingIndex].areas[result.areaIndex] = result.area
            })

            // Step 4: Update the store with the complete hierarchy
            this.setAllLocalesStoreValue(mergeLocaleHierarchy(this.allLocalesStoreValue, {
                lastRefreshTime: new Date().toISOString(),
                regionsLoaded: true,
                initialLoadComplete: true,
                locales: {
                    regions: regionsWithRooms
                }
            }))
        } catch (error) {
            console.error('Error loading remaining locations in background:', {
                error: error.message,
                name: error.name,
                timestamp: new Date().toISOString()
            })

            // Don't show error notification for background failures
            // Regions are already displayed, and on-demand loading can handle missing data
        }
    }

    /**
     * Map `items` through async `asyncFn`, keeping at most `limit` calls in flight at
     * once, and return the results in the SAME ORDER as `items` (identical ordering
     * semantics to `Promise.all(items.map(asyncFn))`, just bounded). Workers pull from
     * a shared cursor so a slow item never blocks others beyond the concurrency cap.
     *
     * @param {Array} items - the input list
     * @param {number} limit - max concurrent in-flight asyncFn calls
     * @param {Function} asyncFn - async (item, index) => result
     * @returns {Promise<Array>} results aligned to `items` order
     */
    async mapWithConcurrencyLimit(items, limit, asyncFn) {
        const results = new Array(items.length)
        let nextIndex = 0

        const worker = async () => {
            while (true) {
                const currentIndex = nextIndex
                nextIndex += 1
                if (currentIndex >= items.length) {
                    return
                }
                results[currentIndex] = await asyncFn(items[currentIndex], currentIndex)
            }
        }

        const workerCount = Math.min(limit, items.length)
        const workers = []
        for (let i = 0; i < workerCount; i += 1) {
            workers.push(worker())
        }
        await Promise.all(workers)

        return results
    }

    /**
     * Fetch all regions with pagination handling
     */
    async fetchAllRegions() {
        const results = []
        let paginationToken = null

        do {
            const response = await fetch('/api/locales/regions/list' + (paginationToken ? `?pagination_token=${ encodeURIComponent(paginationToken) }` : ''), {
                method: 'GET',
                headers: this._localeRequestHeaders(),
                credentials: 'same-origin',

                // Background prefetch — must not starve user-initiated requests (e.g. project selection)
                priority: 'low',
                signal: AbortSignal.timeout(30000)  // 30 second timeout
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${ response.status }`)
            }

            const data = await response.json()

            // Handle malformed response
            if (!data || typeof data !== 'object') {
                throw new Error('Malformed API response')
            }

            // Add regions from this page
            if (data.regions) {
                results.push(...data.regions.map(region => ({
                    id: region.id,
                    text: region.name,
                    locale: 'region'
                })))
            }

            // Update pagination token
            paginationToken = data.pagination_token
        } while (paginationToken != null)

        return results
    }

    /**
     * Fetch all buildings for a region with pagination handling
     */
    async fetchAllBuildings(regionId) {
        const results = []
        let paginationToken = null

        do {
            const params = new URLSearchParams({ region: regionId })
            if (paginationToken) {
                params.append('pagination_token', paginationToken)
            }

            const response = await fetch(`/api/locales/buildings/list?${ params }`, {
                method: 'GET',
                headers: this._localeRequestHeaders(),
                credentials: 'same-origin',
                priority: 'low'  // Background prefetch — must not starve user-initiated requests (e.g. project selection)
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${ response.status }`)
            }

            const data = await response.json()

            // Add buildings from this page
            if (data.buildings) {
                results.push(...data.buildings.map(building => ({
                    id: building.id,
                    text: building.name,
                    locale: 'building'
                })))
            }

            // Update pagination token
            paginationToken = data.pagination_token
        } while (paginationToken != null)

        return results
    }

    /**
     * Fetch all areas for a building with pagination handling
     */
    async fetchAllAreas(regionId, buildingId) {
        const results = []
        let paginationToken = null

        do {
            const params = new URLSearchParams({
                region: regionId,
                building: buildingId
            })
            if (paginationToken) {
                params.append('pagination_token', paginationToken)
            }

            const response = await fetch(`/api/locales/areas/list?${ params }`, {
                method: 'GET',
                headers: this._localeRequestHeaders(),
                credentials: 'same-origin',
                priority: 'low'  // Background prefetch — must not starve user-initiated requests (e.g. project selection)
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${ response.status }`)
            }

            const data = await response.json()

            // Add areas from this page
            if (data.areas) {
                results.push(...data.areas.map(area => ({
                    id: area.id,
                    text: area.name,
                    locale: 'area'
                })))
            }

            // Update pagination token
            paginationToken = data.pagination_token
        } while (paginationToken != null)

        return results
    }

    /**
     * Fetch all rooms for an area with pagination handling
     */
    async fetchAllRooms(regionId, buildingId, areaId) {
        const results = []
        let paginationToken = null

        do {
            const params = new URLSearchParams({
                region: regionId,
                building: buildingId,
                area: areaId
            })
            if (paginationToken) {
                params.append('pagination_token', paginationToken)
            }

            const response = await fetch(`/api/locales/rooms/list?${ params }`, {
                method: 'GET',
                headers: this._localeRequestHeaders(),
                credentials: 'same-origin',
                priority: 'low'  // Background prefetch — must not starve user-initiated requests (e.g. project selection)
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${ response.status }`)
            }

            const data = await response.json()

            // Add rooms from this page
            if (data.rooms) {
                results.push(...data.rooms.map(room => ({
                    id: room.id,
                    text: room.name,
                    locale: 'room'
                })))
            }

            // Update pagination token
            paginationToken = data.pagination_token
        } while (paginationToken != null)

        return results
    }

    /**
     * Build the request headers for locale-list fetches. When an organization-scope token is
     * present (set by pages that need the full org locale namespace, e.g. the Projects
     * management page), forward it as X-Project-Scope-Token so the server widens results to
     * scope: :organization. Absent the token, no scope header is sent and requests stay
     * active-project scoped, exactly as before.
     */
    _localeRequestHeaders() {
        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }

        const token = this.hasProjectScopeTokenValue ? this.projectScopeTokenValue : ''
        if (token) {
            headers['X-Project-Scope-Token'] = token
        }

        return headers
    }

    /**
     * Manually refresh the location hierarchy
     * (Can be called by other controllers if needed)
     */
    refreshHierarchy() {
        // Reset the in-flight guard so the load is not silently swallowed by a stale promise
        // from an earlier (possibly completed or failed) load.
        hierarchyLoadPromise = null
        hierarchyLoadPromise = this.fetchRegionsFirst().finally(() => { hierarchyLoadPromise = null })
    }
}
